import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * Button variants with built-in ripple effect
 *
 * Ripple is enabled by default but can be disabled via the `disableRipple` prop.
 * The ripple uses CSS-only animation for performance.
 */
const buttonVariants = cva(
  // Base styles with ripple support (relative + overflow-hidden)
  "relative inline-flex min-h-[44px] min-w-0 items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:opacity-60 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary: Brand navy
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        primary: "bg-primary text-primary-foreground hover:bg-primary/90",
        // Secondary: White bg with border
        secondary: "border border-border bg-white text-foreground hover:bg-muted/60",
        outline: "border border-border bg-white text-foreground hover:bg-muted/60",
        // Ghost: Transparent, hover bg
        ghost: "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
        // Danger: Red (destructive actions)
        destructive: "bg-red-600 text-white hover:bg-red-700",
        danger: "bg-red-600 text-white hover:bg-red-700",
        link: "text-primary underline-offset-4 hover:underline",
        success: "bg-emerald-600 text-white hover:bg-emerald-700",
        warning: "bg-amber-600 text-white hover:bg-amber-700",
      },
      size: {
        default: "h-11 px-5 py-2.5",
        sm: "h-11 px-4 py-2 text-sm",
        lg: "h-12 px-6 py-2.5 text-base",
        icon: "size-11 p-2.5",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Disable the ripple effect animation
   * @default false
   */
  disableRipple?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, disableRipple = false, onClick, ...props }, ref) => {
    const [ripples, setRipples] = React.useState<Array<{ id: number; x: number; y: number }>>([]);

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (!disableRipple) {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - rect.left;
          const y = e.clientY - rect.top;
          const id = Date.now();

          setRipples((prev) => [...prev, { id, x, y }]);

          // Clean up ripple after animation
          setTimeout(() => {
            setRipples((prev) => prev.filter((r) => r.id !== id));
          }, 600);
        }

        onClick?.(e);
      },
      [disableRipple, onClick]
    );

    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size }), className)}
          ref={ref}
          {...props}
        />
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        onClick={handleClick}
        {...props}
      >
        {/* Ripple elements */}
        {ripples.map((ripple) => (
          <span
            key={ripple.id}
            className="pointer-events-none absolute animate-[ripple_0.6s_ease-out] rounded-full bg-white/20"
            style={{
              left: ripple.x,
              top: ripple.y,
              width: 10,
              height: 10,
              marginLeft: -5,
              marginTop: -5,
              transform: 'scale(0)',
            }}
          />
        ))}
        {props.children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
